/**
 * GS1 character sets — General Specifications 26.0 §7.11.
 *
 * Membership is decided by an explicit character list rather than a regex
 * range, because the sets are deliberately *not* contiguous: CSET 82 includes
 * `!` and `"` but not `#` or `$`, and includes `_` but not backtick. A range
 * like `[!-~]` looks close enough to pass casual review and silently admits
 * characters GS1 excludes, which then survive into a printed barcode.
 *
 * Built once at module load into Sets, so membership is O(1) and the tables
 * stay readable as the specification prints them.
 */
/**
 * CSET 82 — the 82 characters permitted in alphanumeric AI values such as
 * batch/lot (10) and serial (21).
 *
 * Written out in specification order so it can be diffed against the printed
 * table by eye.
 */
const CSET_82_CHARS = "!\"%&'()*+,-./" + // 13 punctuation
    "0123456789" + // 10 digits
    ":;<=>?" + // 6 punctuation
    "ABCDEFGHIJKLMNOPQRSTUVWXYZ" + // 26 upper
    "_" + // 1
    "abcdefghijklmnopqrstuvwxyz"; // 26 lower
/**
 * CSET 64 — the URI-safe subset used by the compressed forms.
 *
 * This is RFC 4648 §5's base64url alphabet **in index order**, which is why it
 * is exported: EPCB §4.1 decodes six bits per character by position in exactly
 * this table, so the decoder indexes into this string rather than keeping a
 * second copy of the same 64 characters. One fact, one place.
 */
export const CSET_64_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ" + "abcdefghijklmnopqrstuvwxyz" + "0123456789" + "-_";
const CSET_64_CHARS = CSET_64_ALPHABET;
/**
 * CSET 39 — uppercase-only, used by CPID (8010).
 *
 * Note it is *not* simply "alphanumerics plus punctuation": it excludes
 * lowercase entirely and admits exactly three punctuation characters, `#`, `-`
 * and `/`. Guessing this set would produce something plausible and wrong, which
 * is why AI 8010 sat marked LOOSE until the toolkit oracle could settle it —
 * see `test/oracle/divergences.ts`, ruling `cpid-charset-39`.
 */
const CSET_39_CHARS = "#-/" + "0123456789" + "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const CSET_82 = new Set(CSET_82_CHARS);
const CSET_64 = new Set(CSET_64_CHARS);
const CSET_39 = new Set(CSET_39_CHARS);
/** Number of characters in each set, exported so tests can assert the sizes. */
export const CHARSET_SIZES = {
    N: 10,
    X: CSET_82.size,
    Y: CSET_39.size,
    Z: CSET_64.size,
};
const SETS = {
    X: CSET_82,
    Y: CSET_39,
    Z: CSET_64,
};
/** Is every character of `value` a member of `charset`? Empty is never valid. */
export function isInCharset(value, charset) {
    if (value.length === 0)
        return false;
    if (charset === "N") {
        for (const ch of value) {
            if (ch < "0" || ch > "9")
                return false;
        }
        return true;
    }
    const set = SETS[charset];
    for (const ch of value) {
        if (!set.has(ch))
            return false;
    }
    return true;
}
//# sourceMappingURL=charset.js.map